import {
  Plus,
  Mail,
  UserCog,
  ChevronLeft,
  ChevronRight,
  Upload,
  Send,
  Hourglass,
  CheckCircle2,
  UserPlus,
} from "lucide-react";
import { useState, useEffect } from "react";
import inviteusersService, {
  InviteListParams,
  InviteMetrics,
} from "../../services/invite-users.service";
import ReinviteConfirmationModal from "../../widgets/ReinviteConfirmationModal";
import RevokeConfirmationModal from "../../widgets/RevokeConfirmationModal";
import Tooltip from "../../widgets/ToolTip";
import { MdBlock } from "react-icons/md";
import { toast } from "react-hot-toast";
import InviteUserFormModal from "../../components/users/InviteUserForm";
import Loader from "../../widgets/Loader";
import BulkUploadModal from "./BulkUploadComponent";
import { SearchFilter, MetricCard } from "./ui";
import Can from "../../components/auth/Can";

function InviteUsers() {
  const [invites, setInvites] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Items per page
  const [searchQuery, setSearchQuery] = useState("");
  const [metrics, setMetrics] = useState<InviteMetrics | null>(null);

  // Modal state management for invite user form
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  // State for modals
  const [showReinviteModal, setShowReinviteModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [selectedInviteId, setSelectedInviteId] = useState<string | null>(null);
  const [selectedInviteEmail, setSelectedInviteEmail] = useState<string | null>(
    null,
  );
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const handleOpenModal = () => setIsUploadModalOpen(true);
  const uploadCloseModal = () => setIsUploadModalOpen(false);

  const fetchInvites = async () => {
    try {
      setIsLoading(true);
      const params: InviteListParams = {
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        ...(searchQuery && { email: searchQuery }),
      };
      const response = await inviteusersService.getInvites(params);

      const normalized = (response?.data?.invitedList || []).map(
        (item: any) => {
          let status = "PENDING";
          if (item.accepted) {
            status = "ACCEPTED";
          } else if (new Date(item.expire_at) < new Date()) {
            status = "EXPIRED";
          }

          return {
            _id: item?._id,
            email: item?.userinfo?.emails?.[0]?.value || "-",
            name:
              item.userinfo?.name?.givenName +
                " " +
                item?.userinfo?.name?.familyName || "-",
            role:
              item?.groups
                ?.flatMap((g: any) => g?.roles?.map((r: any) => r?.role_key))
                ?.join(", ") || "—",
            status,
            invitedAt: item?.invited_date,
            invited_verification_id: item?.invited_verification_id,
            user_created: item?.user_created,
            invited_count: item?.invited_count,
          };
        },
      );
      setTotalCount(response?.data?.totalInvited || 0);
      setMetrics(response?.metrics_data || null);

      setInvites(normalized);
    } catch (error: any) {
      console.error("Error fetching invites:", error);
      // Handle API error response format
      if (error?.response?.data?.error?.error_description) {
        toast.error(error?.response?.data?.error?.error_description);
      } else if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
      } else {
        toast.error("Failed to fetch invites");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // SearchFilter handles its own debounce; refetch when paging or filters change.
  useEffect(() => {
    fetchInvites();
  }, [currentPage, searchQuery, pageSize]);

  // Modal handlers for invite user form
  const handleOpenInviteModal = () => {
    setIsInviteModalOpen(true);
  };

  const handleCloseInviteModal = () => {
    setIsInviteModalOpen(false);
  };

  const handleRefresh = () => {
    setCurrentPage(1); // Reset to first page on refresh
    fetchInvites();
    setIsUploadModalOpen(false);
    setIsInviteModalOpen(false);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1); // Reset to first page when search filters change
  };

  /** Revoke invitation */
  const handleRevokeClick = (inviteId: string, email: string) => {
    setSelectedInviteId(inviteId);
    setSelectedInviteEmail(email);
    setShowRevokeModal(true);
  };

  const confirmRevoke = async () => {
    fetchInvites();
    setShowRevokeModal(false);
    setSelectedInviteId(null);
    setSelectedInviteEmail(null);
  };

  /** Reinvite (Resend) */
  const handleReinviteClick = (inviteId: string, email: string) => {
    setSelectedInviteId(inviteId);
    setSelectedInviteEmail(email);
    setShowReinviteModal(true);
  };

  const confirmReinvite = async () => {
    fetchInvites();
    setShowReinviteModal(false);
    setSelectedInviteId(null);
    setSelectedInviteEmail(null);
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="min-h-screen bg-gray-50">
      <div>
        {/* <Toaster /> */}
        {/* Header */}
        <div className="sticky top-0 z-40 bg-white border-slate-200 border-b mb-4">
          <div className="px-6 py-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  User Invitations
                </h1>
                <p className="text-xs text-slate-600">
                  Manage pending, accepted, and expired invitations.
                </p>
              </div>
              <div className="flex gap-2 items-center justify-end">
                {/* Search Input */}
                <SearchFilter
                  placeholder="Search by email..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-64"
                  debounceMs={400}
                />

                {/* Bulk Upload Button */}
                <Can permission="users:bulk_import">
                <button
                  className="flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-600 hover:text-white transition-colors text-sm font-medium"
                  onClick={handleOpenModal}
                  type="button"
                >
                  <Upload className="w-4 h-4" />
                  Bulk Upload
                </button>
                </Can>

                {/* Invite User Button */}
                <Can permission="users:invite">
                <button
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  type="button"
                  onClick={handleOpenInviteModal} // modal handler
                >
                  <Plus className="w-4 h-4" />
                  Invite User
                </button>
                </Can>
              </div>
            </div>
          </div>
        </div>

        {/* Metric Cards */}
        <div className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              icon={<Send className="w-5 h-5" />}
              label="Total Invites"
              value={metrics?.total_invites ?? 0}
              accent="blue"
              isLoading={!metrics}
            />
            <MetricCard
              icon={<Hourglass className="w-5 h-5" />}
              label="Pending"
              value={metrics?.total_pending ?? 0}
              accent="amber"
              isLoading={!metrics}
            />
            <MetricCard
              icon={<CheckCircle2 className="w-5 h-5" />}
              label="Accepted"
              value={metrics?.total_accepted ?? 0}
              accent="emerald"
              isLoading={!metrics}
            />
            <MetricCard
              icon={<UserPlus className="w-5 h-5" />}
              label="Registered"
              value={metrics?.total_registered ?? 0}
              sub={
                metrics ? `Success rate: ${metrics.success_rate}` : undefined
              }
              accent="violet"
              isLoading={!metrics}
            />
          </div>
        </div>

        {/* Invites Table */}
        <div className="pb-6">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-base font-semibold text-gray-900">
                Invitations List
              </h3>
              <div className="text-sm text-gray-500">
                {totalCount > 0 &&
                  `Showing ${startItem}-${endItem} of ${totalCount} invites`}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Roles
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Invited
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-6 py-12 text-center text-gray-500"
                      >
                        <Loader />
                      </td>
                    </tr>
                  ) : invites?.length > 0 ? (
                    invites.map((invite: any) => {
                      const reinviteDisabled =
                        invite?.user_created || invite?.invited_count >= 6;
                      const revokeDisabled = invite?.user_created;
                      const reinviteTooltip = invite?.user_created
                        ? "User already created"
                        : invite?.invited_count >= 6
                          ? "Maximum invites reached"
                          : "Reinvite";
                      const revokeTooltip = invite?.user_created
                        ? "Cannot revoke — user already created"
                        : "Revoke";

                      return (
                        <tr
                          key={invite?._id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-semibold text-sm">
                                {(invite?.name || invite?.email || "?")
                                  .charAt(0)
                                  .toUpperCase()}
                              </div>
                              <div className="text-sm font-medium text-gray-900">
                                {invite?.name || "—"}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2 text-sm text-gray-700">
                              <Mail className="w-4 h-4 text-gray-400" />
                              {invite?.email}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700 align-top max-w-[260px] whitespace-normal break-words">
                            {invite?.role || "—"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full ${
                                invite?.status === "ACCEPTED"
                                  ? "bg-emerald-50 text-emerald-700"
                                  : invite?.status === "PENDING"
                                    ? "bg-amber-50 text-amber-700"
                                    : invite?.status === "EXPIRED"
                                      ? "bg-red-50 text-red-700"
                                      : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${
                                  invite?.status === "ACCEPTED"
                                    ? "bg-emerald-500"
                                    : invite?.status === "PENDING"
                                      ? "bg-amber-500"
                                      : invite?.status === "EXPIRED"
                                        ? "bg-red-500"
                                        : "bg-gray-400"
                                }`}
                              />
                              {invite?.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {invite?.invitedAt ? (
                              <div className="leading-tight">
                                <div className="text-gray-700">
                                  {new Date(
                                    invite.invitedAt,
                                  ).toLocaleDateString(undefined, {
                                    year: "numeric",
                                    month: "short",
                                    day: "2-digit",
                                  })}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {new Date(
                                    invite.invitedAt,
                                  ).toLocaleTimeString(undefined, {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </div>
                              </div>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-1">
                              <Can permission="users:invite">
                              <Tooltip content={reinviteTooltip} position="top">
                                <button
                                  disabled={reinviteDisabled}
                                  className={`p-2 rounded-lg transition-colors ${
                                    reinviteDisabled
                                      ? "text-gray-300 cursor-not-allowed"
                                      : "text-emerald-600 hover:bg-emerald-50"
                                  }`}
                                  type="button"
                                  onClick={() =>
                                    handleReinviteClick(
                                      invite?.invited_verification_id,
                                      invite?.email,
                                    )
                                  }
                                >
                                  <UserCog className="w-4 h-4" />
                                </button>
                              </Tooltip>
                              </Can>

                              <Can permission="users:invite">
                              <Tooltip content={revokeTooltip} position="top">
                                <button
                                  disabled={revokeDisabled}
                                  className={`p-2 rounded-lg transition-colors ${
                                    revokeDisabled
                                      ? "text-gray-300 cursor-not-allowed"
                                      : "text-red-600 hover:bg-red-50"
                                  }`}
                                  type="button"
                                  onClick={() =>
                                    handleRevokeClick(
                                      invite?.invited_verification_id,
                                      invite?.email,
                                    )
                                  }
                                >
                                  <MdBlock className="w-4 h-4" />
                                </button>
                              </Tooltip>
                              </Can>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-6 py-12 text-center text-gray-500"
                      >
                        No invitations found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalCount > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-700">
                    Showing <span className="font-medium">{startItem}</span> to{" "}
                    <span className="font-medium">{endItem}</span> of{" "}
                    <span className="font-medium">{totalCount}</span> results
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Show:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      type="button"
                      className="inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= totalPages - 2)
                        pageNum = totalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          type="button"
                          className={`inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === pageNum
                              ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                              : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      type="button"
                      className="inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Invite User Form Modal */}
        <InviteUserFormModal
          isOpen={isInviteModalOpen}
          onClose={handleCloseInviteModal}
          onRefresh={handleRefresh}
        />
        {/* Bulk Upload Form Modal */}
        <BulkUploadModal
          isOpen={isUploadModalOpen}
          onClose={uploadCloseModal}
          onRefresh={handleRefresh}
          callBackFunction={fetchInvites}
        />

        {/* Reinvite Confirmation Modal */}
        <ReinviteConfirmationModal
          isOpen={showReinviteModal}
          onClose={() => setShowReinviteModal(false)}
          userName={selectedInviteEmail || ""}
          userId={selectedInviteId || ""}
          onConfirm={confirmReinvite}
        />

        {/* Revoke Confirmation Modal */}
        <RevokeConfirmationModal
          isOpen={showRevokeModal}
          onClose={() => setShowRevokeModal(false)}
          userName={selectedInviteEmail || ""}
          userId={selectedInviteId || ""}
          onConfirm={confirmRevoke}
        />
      </div>
    </div>
  );
}

export default InviteUsers;
